import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Folder, File as FileIcon, DownloadSimple, LockKey, ShieldWarning, Image as ImageIcon,
  VideoCamera, FileText, CaretRight, House, Archive
} from "@phosphor-icons/react";

const RAW = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

function prettySize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fileIconFor(mime) {
  if (!mime) return FileIcon;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return VideoCamera;
  if (mime === "application/pdf" || mime.startsWith("text/")) return FileText;
  return FileIcon;
}

export default function PublicSharePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [view, setView] = useState(null);  // folder/file contents after unlock
  const [breadcrumb, setBreadcrumb] = useState([]);  // [{id, name}]
  const [submitting, setSubmitting] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await RAW.get(`/public/share/${token}`);
      setMeta(data);
      // If no password, auto-unlock
      if (!data.has_password) {
        await unlock("");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Link unavailable");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  async function unlock(pw) {
    setSubmitting(true);
    try {
      const { data } = await RAW.post(`/public/share/${token}/verify`, { password: pw });
      setView(data);
      setUnlocked(true);
      if (data.folder) setBreadcrumb([{ id: data.folder.id, name: data.folder.name }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not unlock";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  async function openChild(folderId, name) {
    setSubmitting(true);
    try {
      const { data } = await RAW.post(`/public/share/${token}/child/${folderId}`, { password });
      setView({ ...data, target_type: "folder", allow_download: data.allow_download });
      setBreadcrumb((bc) => [...bc, { id: folderId, name }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot open folder");
    } finally {
      setSubmitting(false);
    }
  }

  async function goBreadcrumb(index) {
    if (index === breadcrumb.length - 1) return;
    const target = breadcrumb[index];
    if (index === 0) {
      await unlock(password);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await RAW.post(`/public/share/${token}/child/${target.id}`, { password });
      setView({ ...data, target_type: "folder", allow_download: data.allow_download });
      setBreadcrumb(breadcrumb.slice(0, index + 1));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot open folder");
    } finally {
      setSubmitting(false);
    }
  }

  async function downloadFile(fileId, name) {
    try {
      const res = await RAW.post(`/public/share/${token}/download/${fileId}`,
        { password }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  }

  // "Download all as ZIP" — bundles the entire shared folder tree in one
  // click. Works even for anonymous viewers (no login) as long as the token
  // + password (if any) validate. Errors arrive as JSON-in-a-blob so we parse
  // before showing a toast, exactly like the internal /files/bulk-download.
  async function downloadShareZip() {
    setZipBusy(true);
    const toastId = toast.loading("Preparing ZIP…");
    try {
      const res = await RAW.post(`/public/share/${token}/download-zip`,
        { password }, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 10);
      const rootName = view?.folder?.name || breadcrumb[0]?.name || "shared";
      const safeName = rootName.replace(/[^A-Za-z0-9._\- ]+/g, "_").trim();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.dismiss(toastId);
      // Streaming ZIP: X-Files-Included is the attempted count; the actual
      // delivered count only surfaces once the recipient extracts the ZIP.
      // Keep the message honest.
      toast.success("Download ready — check your downloads folder.");
    } catch (e) {
      toast.dismiss(toastId);
      // Blob error → parse JSON detail
      let detail = "ZIP download failed";
      try {
        const blob = e.response?.data;
        if (blob && blob.type?.includes("json")) {
          const text = await blob.text();
          detail = JSON.parse(text).detail || detail;
        }
      } catch { /* ignore parse errors */ }
      toast.error(detail);
    } finally { setZipBusy(false); }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-lg shadow p-8 text-center">
          <ShieldWarning className="h-10 w-10 text-amber-500 mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-slate-900 dark:text-slate-100">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  if (!unlocked && meta.has_password) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-lg shadow p-8" data-testid="public-share-password-form">
          <LockKey className="h-10 w-10 text-blue-500 mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-slate-900 dark:text-slate-100 text-center">
            Password required
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 text-center">
            This {meta.target_type} is protected. Enter the password shared with you.
          </p>
          <div className="mt-6 space-y-3">
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && unlock(password)}
                     data-testid="public-share-password-input" />
            </div>
            <Button disabled={submitting} onClick={() => unlock(password)} className="w-full" data-testid="public-share-unlock-btn">
              {submitting ? "Unlocking…" : "Unlock"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render unlocked view
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow">
          <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Shared {meta.target_type}</p>
              <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-1">
                {view?.folder?.name || view?.file?.name || "Shared"}
              </h1>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              {meta.expires_at && <span>Expires {new Date(meta.expires_at).toLocaleDateString()}</span>}
              {!meta.allow_download && <span className="text-amber-600">View-only</span>}
            </div>
          </div>

          {view?.target_type === "folder" && (
            <div className="p-6 space-y-4" data-testid="public-share-folder">
              {!!breadcrumb.length && (
                <nav className="flex items-center justify-between flex-wrap gap-2 text-sm text-slate-600">
                  <div className="flex items-center flex-wrap gap-1">
                    <House className="h-4 w-4" />
                    {breadcrumb.map((c, i) => (
                      <span key={c.id} className="inline-flex items-center gap-1">
                        <button onClick={() => goBreadcrumb(i)}
                                className={i === breadcrumb.length - 1 ? "font-semibold text-slate-900 dark:text-slate-100" : "hover:text-slate-900 dark:hover:text-slate-200"}>
                          {c.name}
                        </button>
                        {i < breadcrumb.length - 1 && <CaretRight className="h-3.5 w-3.5 text-slate-400" />}
                      </span>
                    ))}
                  </div>
                  {meta.allow_download && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={downloadShareZip}
                      disabled={zipBusy}
                      className="shrink-0"
                      data-testid="public-share-download-zip-btn"
                      title="Download the entire shared folder + all subfolders as a single ZIP"
                    >
                      <Archive className="h-4 w-4 mr-1.5" />
                      {zipBusy ? "Zipping…" : "Download all as ZIP"}
                    </Button>
                  )}
                </nav>
              )}

              {!!view.children?.length && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Folders</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {view.children.map((f) => (
                      <button key={f.id} onClick={() => openChild(f.id, f.name)}
                              className="flex items-center gap-2 p-3 rounded-md border border-slate-200 dark:border-slate-700 hover:border-blue-400 transition text-left"
                              data-testid={`public-share-folder-${f.id}`}>
                        <Folder className="h-5 w-5 text-blue-500 shrink-0" />
                        <span className="text-sm font-medium truncate">{f.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!!view.files?.length ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Files</p>
                  <div className="space-y-1">
                    {view.files.map((f) => {
                      const Icon = fileIconFor(f.mime_type);
                      return (
                        <div key={f.id} className="flex items-center gap-3 p-2 rounded-md border border-slate-200 dark:border-slate-700" data-testid={`public-share-file-${f.id}`}>
                          <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{f.name}</p>
                            <p className="text-[11px] text-slate-500">{prettySize(f.size)}</p>
                          </div>
                          {view.allow_download && (
                            <Button size="sm" variant="outline" onClick={() => downloadFile(f.id, f.name)} data-testid={`public-share-download-${f.id}`}>
                              <DownloadSimple className="h-4 w-4 mr-1" /> Download
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (!view.children?.length && (
                <p className="text-sm text-slate-500 text-center py-8">This folder is empty.</p>
              ))}
            </div>
          )}

          {view?.target_type === "file" && view.file && (
            <div className="p-6 text-center" data-testid="public-share-file">
              {(() => {
                const Icon = fileIconFor(view.file.mime_type);
                return <Icon className="h-16 w-16 text-slate-400 mx-auto" />;
              })()}
              <p className="mt-4 text-lg font-medium text-slate-900 dark:text-slate-100">{view.file.name}</p>
              <p className="text-sm text-slate-500 mt-1">{prettySize(view.file.size)}</p>
              {view.allow_download && (
                <Button className="mt-6" onClick={() => downloadFile(view.file.id, view.file.name)} data-testid="public-share-single-download-btn">
                  <DownloadSimple className="h-4 w-4 mr-2" /> Download file
                </Button>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 text-center mt-4">
          Powered by Facilities Management Platform
        </p>
      </div>
    </div>
  );
}
